import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { IRaffleAffiliateDTO } from "@hooks/dtos/raffles/IRaffleAffiliateDTO";
import {
  IRaffleDefaultTicketPurchaseAffiliateSummaryDTO,
  IRaffleDefaultTicketPurchaseListDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultTicketPurchaseDTO";
import {
  RaffleDefaultTicketPurchaseStatusEnum,
  RaffleDefaultTicketPurchaseStatusTypes,
} from "@hooks/enums/raffle_default_ticket_purchase";
import { abbreviateName, formatName } from "@hooks/functions/formatName";
import { Masks } from "@hooks/functions/Masks/Masks";
import {
  getRaffleAffiliateStatus,
  getRaffleDefaultTicketPurchaseStatus,
  raffleDefaultTicketPurchaseStatusSelect,
} from "@hooks/functions/Raffle/status";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  FaChartLine,
  FaEye,
  FaEyeLowVision,
  FaHandshake,
  FaReceipt,
  FaTrophy,
} from "react-icons/fa6";

interface ILoadingDTO {
  affiliate: boolean;
  summary: boolean;
  table: boolean;
}

interface IProps {
  raffleKey: string;
  affiliateId: number;
  isOpen: boolean;
  onClose: () => void;
}

const masks = new Masks();
export function ModalRaffleAffiliateSummary({
  raffleKey,
  affiliateId,
  isOpen,
  onClose,
}: IProps) {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const [animate, setAnimate] = useState(false);
  const [isLoading, setIsLoading] = useState<ILoadingDTO>({
    affiliate: true,
    summary: true,
    table: true,
  });

  // Tab control
  const [activeTab, setActiveTab] = useState<"summary" | "history">("summary");

  // Filter collapse control
  const [showFees, setShowFees] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState(false);

  // Pagination Part
  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [order, setOrder] = useState("DESC");
  const [totalPages, setTotalPages] = useState<number>(1);

  // Filters
  const [startAt, setStartDate] = useState("");
  const [endAt, setEndDate] = useState("");
  const [paymentStatus, setPaymentStatus] = useState<
    RaffleDefaultTicketPurchaseStatusTypes | undefined
  >(
    RaffleDefaultTicketPurchaseStatusEnum.PAID as RaffleDefaultTicketPurchaseStatusTypes,
  );

  const [affiliate, setAffiliate] = useState<IRaffleAffiliateDTO>();
  const [histories, setHistories] =
    useState<IRaffleDefaultTicketPurchaseListDTO[]>();
  const [summary, setSummary] =
    useState<IRaffleDefaultTicketPurchaseAffiliateSummaryDTO>();

  // Fetch affiliate data
  const handleRaffleAffiliate = async () => {
    try {
      const params = `?key=${raffleKey}&affiliate=${affiliateId}`;
      const result = await api.get(`/customer/raffle/affiliate${params}`);
      setAffiliate(result.data);

      setIsLoading((prev) => ({
        ...prev,
        affiliate: false,
      }));
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ??
          "Ocorreu um erro ao carregar os dados!",
        type: "error",
        theme: "dark",
      });
      setIsLoading((prev) => ({
        ...prev,
        affiliate: false,
      }));
    }
  };

  // Fetch Affiliate Summary data
  const handleRaffleAffiliateSummary = async () => {
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          ...(raffleKey && { key: raffleKey }),
          ...(affiliateId && { commissionTo: String(affiliateId) }),
          ...(paymentStatus && { paymentStatus }),
          ...(startAt && { startAt }),
          ...(endAt && { endAt }),
        }),
      ).toString()}`;
      const result = await api.get(
        `/customer/raffle/affiliate/summary${params}`,
      );
      setSummary(result.data);
      console.log({ f: "Summary", result });

      setIsLoading((prev) => ({
        ...prev,
        summary: false,
      }));
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ??
          "Ocorreu um erro ao carregar os dados!",
        type: "error",
        theme: "dark",
      });
      setIsLoading((prev) => ({
        ...prev,
        summary: false,
      }));
    }
  };

  // Fetch Affiliate Earning List
  const handleRaffleAffiliateSaleList = async () => {
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          ...(raffleKey && { key: raffleKey }),
          ...(affiliateId && { commissionTo: String(affiliateId) }),
          ...(paymentStatus && { status: paymentStatus }),
          ...(startAt && { startAt }),
          ...(endAt && { endAt }),
          ...(page && { page: String(page) }),
          ...(take && { take: String(take) }),
          ...(order && { order }),
        }),
      ).toString()}`;
      const result = await api.get(
        `/customer/raffle/affiliate/saleList${params}`,
      );
      setHistories(result.data.list);
      setTotalPages(result.data.totalPages);
      setIsLoading((prev) => ({
        ...prev,
        table: false,
      }));
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ??
          "Ocorreu um erro ao carregar os dados!",
        type: "error",
        theme: "dark",
      });
      setIsLoading((prev) => ({
        ...prev,
        table: false,
      }));
    }
  };

  function handleCancel() {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }

  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      handleCancel();
    }
  }

  function handleApplyFilters() {
    handleRaffleAffiliateSummary();
    handleRaffleAffiliateSaleList();
  }

  function handleClearFilters() {
    setStartDate("");
    setEndDate("");
    setPaymentStatus(
      RaffleDefaultTicketPurchaseStatusEnum.PAID as RaffleDefaultTicketPurchaseStatusTypes,
    );
    // Reload data with cleared filters
    setTimeout(() => {
      handleRaffleAffiliateSummary();
      handleRaffleAffiliateSaleList();
    }, 100);
  }

  useEffect(() => {
    if (isOpen) {
      handleRaffleAffiliate();
      handleRaffleAffiliateSummary();
      handleRaffleAffiliateSaleList();

      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeTab === "history" && isOpen) {
      handleRaffleAffiliateSaleList();
    }
  }, [page, take, order]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="staticBackdropLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header border-0 pb-2">
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h5
                    className="modal-title mb-0 fw-bold"
                    id="contentModallLabel"
                  >
                    <FaChartLine className="me-2" />
                    Desempenho do Afiliado
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCancel}
                  ></button>
                </div>

                {/* Affiliate Header Info */}
                {isLoading.affiliate ? (
                  <div className="text-center py-3">
                    <LoadingSkeleton />
                  </div>
                ) : (
                  affiliate?.customerData && (
                    <>
                      {(() => {
                        const affiliateStatus = getRaffleAffiliateStatus(
                          affiliate.status,
                        );

                        return (
                          <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                            {/* Left: Avatar and Status */}
                            <div className="flex-shrink-0 text-center">
                              {affiliate.customerData.avatar ? (
                                <img
                                  src={affiliate.customerData.avatar}
                                  alt={affiliate.customerData.name}
                                  className="rounded-circle border border-3 border-primary"
                                  style={{
                                    width: "70px",
                                    height: "70px",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle bg-gradient-primary d-inline-flex align-items-center justify-content-center fw-bold border border-3 border-white shadow"
                                  style={{
                                    width: "70px",
                                    height: "70px",
                                    fontSize: "28px",
                                  }}
                                >
                                  {affiliate.customerData.name
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>
                              )}
                              <div className="mt-2">
                                <span
                                  className={`badge bg-${affiliateStatus?.color} px-2 py-1 fs-6`}
                                  title={affiliateStatus?.text}
                                >
                                  {affiliateStatus?.title}
                                </span>
                              </div>
                            </div>

                            {/* Right: Info */}
                            <div className="flex-grow-1 ms-3">
                              <h5 className="mb-1 fw-bold">
                                {formatName(affiliate.customerData.name || "")}
                              </h5>
                              <p className="mb-0">
                                <span className="badge bg-info text-white py-2 px-3 fs-6">
                                  @{affiliate.customerData.atSign}
                                </span>
                                <span className="mx-2">•</span>
                                <span className="badge bg-outline-info text-black py-2 px-3 fs-6 text-center">
                                  Comissão {affiliate.percent}%
                                </span>
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )
                )}

                {/* Filters Section */}
                <div className="mb-3">
                  <button
                    className="btn btn-sm btn-outline-primary w-100 d-flex align-items-center justify-content-between"
                    onClick={() => setShowFilters(!showFilters)}
                    type="button"
                  >
                    <span>
                      <i className="fas fa-filter me-2"></i>
                      Filtros Avançados
                    </span>
                    <i
                      className={`fas fa-chevron-${showFilters ? "up" : "down"}`}
                    ></i>
                  </button>

                  {showFilters && (
                    <div className="card border-0 shadow-sm mt-2">
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6 col-lg-3">
                            <label
                              htmlFor="startAt"
                              className="form-label small"
                            >
                              <i className="fas fa-calendar-alt me-1"></i>
                              Data Inicial
                            </label>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              onChange={(e) => setStartDate(e.target.value)}
                              id="startAt"
                              value={startAt}
                            />
                            <small className="text-muted">
                              Data da venda dos tickets
                            </small>
                          </div>

                          <div className="col-md-6 col-lg-3">
                            <label htmlFor="endAt" className="form-label small">
                              <i className="fas fa-calendar-check me-1"></i>
                              Data Final
                            </label>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              onChange={(e) => setEndDate(e.target.value)}
                              id="endAt"
                              value={endAt}
                            />
                            <small className="text-muted">
                              Data da venda dos tickets
                            </small>
                          </div>

                          <div className="col-md-12 col-lg-6">
                            <label
                              htmlFor="Status"
                              className="form-label small"
                            >
                              <i className="fas fa-tag me-1"></i>
                              Status da Aquisição
                            </label>
                            <select
                              className="form-select form-select-sm"
                              aria-label="paymentStatus"
                              value={paymentStatus || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPaymentStatus(
                                  value === ""
                                    ? undefined
                                    : (value as RaffleDefaultTicketPurchaseStatusTypes),
                                );
                              }}
                            >
                              <option value="">Todos os Status</option>
                              {raffleDefaultTicketPurchaseStatusSelect.map(
                                (x, index) => {
                                  return (
                                    <option key={index} value={x.status}>
                                      {x.text}
                                    </option>
                                  );
                                },
                              )}
                            </select>
                          </div>
                        </div>

                        <div className="d-flex gap-2 mt-3">
                          <button
                            className="btn btn-sm btn-primary flex-grow-1"
                            onClick={handleApplyFilters}
                          >
                            <i className="fas fa-check me-2"></i>
                            Aplicar Filtros
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleClearFilters}
                          >
                            <i className="fas fa-times me-2"></i>
                            Limpar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tabs Navigation */}
                <ul className="nav nav-tabs nav-line nav-color-primary">
                  <li className="nav-item">
                    <a
                      className={`nav-link ${activeTab === "summary" ? "active" : ""}`}
                      onClick={() => setActiveTab("summary")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="fas fa-chart-pie me-2"></i>
                      Resumo
                    </a>
                  </li>
                  <li className="nav-item">
                    <a
                      className={`nav-link ${activeTab === "history" ? "active" : ""}`}
                      onClick={() => setActiveTab("history")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="fas fa-history me-2"></i>
                      Histórico de Vendas
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="modal-body pt-3">
              {/* Summary Tab */}
              {activeTab === "summary" && (
                <div className="tab-content-wrapper">
                  {isLoading.summary ? (
                    <LoadingSkeleton />
                  ) : summary ? (
                    <>
                      {/* Overall Performance */}
                      <div className="">
                        <div className="d-flex">
                          <FaTrophy className="fs-5 me-2 text-muted" />
                          <h6 className="text-uppercase text-muted mb-3 fw-bold">
                            Desempenho Geral
                          </h6>
                        </div>
                        <div className="row g-3">
                          <div className="col-6 col-md-3">
                            <div className="card border-0 shadow-sm">
                              <div className="card-body text-center p-3">
                                <div className="text-muted small mb-1">
                                  Bilhetes Vendidos
                                </div>
                                <h4 className="mb-0 text-primary fw-bold">
                                  {summary.ticketSoldAmount}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="card border-0 shadow-sm">
                              <div className="card-body text-center p-3">
                                <div className="text-muted small mb-1">
                                  Faturamento Bruto
                                </div>
                                <h4 className="mb-0 text-success fw-bold">
                                  {masks.brlCurrency(summary.inflowGrossTotal)}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="card border-0 shadow-sm">
                              <div className="card-body text-center p-3">
                                <div className="text-muted small mb-1">
                                  Faturamento Líquido
                                </div>
                                <h4 className="mb-0 text-info fw-bold">
                                  {masks.brlCurrency(summary.inflowTotal)}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="card border-0 shadow-sm">
                              <div className="card-body text-center p-3">
                                <div className="text-muted small mb-1">
                                  Total em Taxas
                                </div>
                                <h4 className="mb-0 text-danger fw-bold">
                                  {masks.brlCurrency(
                                    summary.externalFeeTotal +
                                      summary.internalFeeTotal +
                                      summary.anotherFeeTotal,
                                  )}
                                </h4>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fee Breakdown */}
                      <div className="">
                        <div
                          className="d-flex"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setShowFees(!showFees);
                          }}
                        >
                          <FaReceipt className="fs-5 me-2 text-muted" />
                          <h6 className="text-uppercase text-muted mb-3 fw-bold">
                            Detalhamento de Taxas
                          </h6>
                          {showFees ? (
                            <FaEyeLowVision className="fs-5 ms-2 text-muted" />
                          ) : (
                            <FaEye className="fs-5 ms-2 text-muted" />
                          )}
                        </div>
                        <div className={`row g-3 ${showFees ? `` : `d-none`}`}>
                          <div className="col-md-4">
                            <div className="card border-start border-danger border-4">
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="text-muted small">
                                      Taxa Externa
                                    </div>
                                    <h5 className="mb-0">
                                      {masks.brlCurrency(
                                        summary.externalFeeTotal,
                                      )}
                                    </h5>
                                  </div>
                                  <i className="fas fa-external-link-alt fa-2x text-danger opacity-25"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="card border-start border-warning border-4">
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="text-muted small">
                                      Taxa Interna
                                    </div>
                                    <h5 className="mb-0">
                                      {masks.brlCurrency(
                                        summary.internalFeeTotal,
                                      )}
                                    </h5>
                                  </div>
                                  <i className="fas fa-home fa-2x text-warning opacity-25"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="card border-start border-secondary border-4">
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="text-muted small">
                                      Outras Taxas
                                    </div>
                                    <h5 className="mb-0">
                                      {masks.brlCurrency(
                                        summary.anotherFeeTotal,
                                      )}
                                    </h5>
                                  </div>
                                  <i className="fas fa-ellipsis-h fa-2x text-secondary opacity-25"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Affiliate Performance */}
                      <div className="">
                        <div className="d-flex">
                          <FaHandshake className="fs-4 me-2 text-muted" />
                          <h6 className="text-uppercase text-muted mb-3 fw-bold">
                            Desempenho do Afiliado
                          </h6>
                        </div>
                        <div className="row g-3">
                          <div className="col-6 col-md-3">
                            <div className="card bg-gradient-primary">
                              <div className="card-body p-3">
                                <div className="small mb-1 opacity-75">
                                  Bilhetes Vendidos
                                </div>
                                <h4 className="mb-0 fw-bold">
                                  {summary.ticketAffiliateSoldAmount}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="card bg-gradient-success">
                              <div className="card-body p-3">
                                <div className="small mb-1 opacity-75">
                                  Faturamento Bruto
                                </div>
                                <h4 className="mb-0 fw-bold">
                                  {masks.brlCurrency(
                                    summary.inflowAffiliateGrossTotal,
                                  )}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="card bg-gradient-info">
                              <div className="card-body p-3">
                                <div className="small mb-1 opacity-75">
                                  Faturamento Líquido
                                </div>
                                <h4 className="mb-0 fw-bold">
                                  {masks.brlCurrency(
                                    summary.inflowAffiliateTotal,
                                  )}
                                </h4>
                              </div>
                            </div>
                          </div>
                          <div className="col-6 col-md-3">
                            <div className="card bg-gradient-warning">
                              <div className="card-body p-3">
                                <div className="small mb-1 opacity-75">
                                  Comissão Recebida
                                </div>
                                <h4 className="mb-0 fw-bold">
                                  {masks.brlCurrency(summary.commissionTotal)}
                                </h4>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Performance Indicator */}
                      <div>
                        <div className="alert alert-info border-0 shadow-sm mb-0">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-info-circle fa-2x me-3"></i>
                            <div>
                              <strong>Taxa de Conversão:</strong>{" "}
                              {(summary.ticketSoldAmount
                                ? (summary.ticketAffiliateSoldAmount /
                                    summary.ticketSoldAmount) *
                                  100
                                : 0
                              ).toFixed(2)}
                              % dos bilhetes vendidos foram através deste
                              afiliado
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-exclamation-circle fa-3x text-muted mb-3"></i>
                      <p className="text-muted">
                        Nenhum dado de resumo disponível.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === "history" && (
                <div className="tab-content-wrapper">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th className="text-center" style={{ width: "60px" }}>
                            #
                          </th>
                          <th style={{ minWidth: "8rem" }}>Cliente</th>
                          <th>Valor Bruto</th>
                          <th>Tx Interna</th>
                          <th>Tx Externa</th>
                          <th>Tx Outras</th>
                          <th>Comissão (R$)</th>
                          <th>Lucro Líquido</th>
                          <th>Comissão (%)</th>
                          <th className="text-center">Status</th>
                          <th style={{ minWidth: "9rem" }}>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading.table ? (
                          <tr>
                            <td colSpan={10} className="text-center py-5">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        ) : histories && histories.length > 0 ? (
                          histories.map((history) => {
                            const paymentStatus =
                              getRaffleDefaultTicketPurchaseStatus(
                                history.status,
                              );
                            const clientName = history?.clientData?.name
                              ? abbreviateName(history?.clientData?.name, 12)
                              : "~";
                            return (
                              <tr key={history.id}>
                                <td className="text-center">
                                  <span className="badge bg-light text-dark">
                                    # {history.id}
                                  </span>
                                </td>
                                <td className="">
                                  <div className="d-flex flex-column">
                                    <span>{clientName}</span>
                                    <small className="text-muted">
                                      @{history?.clientData?.atSign || "~"}
                                    </small>
                                  </div>
                                </td>
                                <td className="fw-bold text-primary">
                                  {masks.brlCurrency(history.amountGross)}
                                </td>
                                <td className="text-muted">
                                  {masks.brlCurrency(history.internalFee)}
                                </td>
                                <td className="text-muted">
                                  {masks.brlCurrency(history.externalFee)}
                                </td>
                                <td className="text-muted">
                                  {masks.brlCurrency(history.anotherFee)}
                                </td>
                                <td className="fw-bold text-info">
                                  {masks.brlCurrency(history.commission)}
                                </td>
                                <td className="fw-bold text-success">
                                  {masks.brlCurrency(history.amount)}
                                </td>
                                <td>
                                  <span className="badge bg-primary">
                                    {history.commissionPercent}%
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span
                                    className={`badge bg-${paymentStatus.color}`}
                                    title={paymentStatus.text}
                                  >
                                    {paymentStatus.title}
                                  </span>
                                </td>
                                <td className="text-muted small">
                                  {dayjs(history.createdAt).format(
                                    "DD/MM/YYYY  [ás] HH:MM:ss",
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={10} className="text-center py-5">
                              <i className="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
                              <p className="text-muted mb-0">
                                Nenhum registro encontrado.
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {histories && histories.length > 0 && (
                    <div className="mt-3">
                      <Pagination
                        page={page}
                        setPage={setPage}
                        totalPages={totalPages}
                        maxPageShow={3}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer border-0 pt-0">
              <button
                type="button"
                className="btn btn-outline-secondary rounded px-5"
                onClick={handleCancel}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
}
